import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, Pressable } from "react-native-web";
import { useNavigate, useLocation } from "react-router-dom";
import CustomText from "../../components/atoms/CustomText";
import { colors, spacing, typography } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import { INFO_COLLECTION_QUESTIONS } from "../../utils/infoCollectionQuestions";
import { useUpdateCurrentProfile } from "../../lib/profiles";
import { useAuthSession } from "../../lib/auth";

const NUM_SECTIONS = INFO_COLLECTION_QUESTIONS.length;

interface LocationState {
  from?: string;
  quizId?: number;
  email?: string;
  sfAccount?: string;
}

const InfoCollectionQuestion: React.FC<{
  question: string;
  options: string[];
  value: string | undefined;
  onChange: (value: string) => void;
  onNext: () => void;
}> = ({ question, options, value, onChange, onNext }) => {
  const { isMobile } = useResponsive();

  const handleSelectOption = (option: string) => {
    onChange(option);
  };

  // Calculate layout for 2 columns
  const rows = Math.ceil(options.length / 2);

  return (
    <View
      style={{
        backgroundColor: colors.white,
        borderRadius: 16,
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
        padding: isMobile ? spacing.md : spacing.xl,
        maxWidth: 900,
        width: "100%",
        marginBottom: spacing.xxl,
      }}
    >
      {/* Question */}
      <CustomText
        style={{
          fontSize: isMobile ? 24 : 36,
          fontWeight: "600",
          color: colors.dark,
          fontFamily: "Nunito",
          textAlign: "center",
          marginBottom: spacing.xl,
        }}
      >
        {question}
      </CustomText>

      {/* Options in 2 columns */}
      <View
        style={{
          display: "flex",
          flexDirection: "column",
          gap: spacing.md,
          width: "100%",
        }}
      >
        {Array.from({ length: rows }, (_, rowIndex) => (
          <View
            key={rowIndex}
            style={{
              display: "flex",
              flexDirection: "row",
              gap: spacing.md,
              width: "100%",
            }}
          >
            {/* First column option */}
            {options[rowIndex * 2] && (
              <Pressable
                onPress={() => handleSelectOption(options[rowIndex * 2])}
                style={{
                  backgroundColor:
                    value === options[rowIndex * 2]
                      ? colors.primary
                      : colors.white,
                  boxShadow: value === options[rowIndex * 2]
                    ? '0 4px 24px 0 rgba(30, 41, 59, 0.18)'
                    : undefined,
                  borderRadius: 12,
                  padding: spacing.lg,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  cursor: "pointer",
                  minHeight: isMobile ? 60 : 80,
                  position: "relative",
                  border: '1px solid black',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.boxShadow = '0 4px 24px 0 rgba(30, 41, 59, 0.12)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.boxShadow = value === options[rowIndex * 2]
                    ? '0 4px 24px 0 rgba(30, 41, 59, 0.18)'
                    : '';
                }}
              >
                <CustomText
                  style={{
                    fontSize: isMobile ? 16 : 20,
                    fontWeight: value === options[rowIndex * 2] ? "700" : "500",
                    fontFamily: "Nunito",
                    color: value === options[rowIndex * 2] ? colors.white : colors.gray[700],
                    textAlign: "center",
                    textShadow: value === options[rowIndex * 2]
                      ? '0 2px 8px rgba(30, 41, 59, 0.18)'
                      : undefined,
                    transition: "all 0.2s",
                  }}
                >
                  {options[rowIndex * 2]}
                </CustomText>
              </Pressable>
            )}

            {/* Second column option */}
            {options[rowIndex * 2 + 1] && (
              <Pressable
                onPress={() => handleSelectOption(options[rowIndex * 2 + 1])}
                style={{
                  backgroundColor:
                    value === options[rowIndex * 2 + 1]
                      ? colors.primary
                      : colors.white,
                  boxShadow: value === options[rowIndex * 2 + 1]
                    ? '0 4px 24px 0 rgba(30, 41, 59, 0.18)'
                    : undefined,
                  borderRadius: 12,
                  padding: spacing.lg,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  cursor: "pointer",
                  minHeight: isMobile ? 60 : 80,
                  position: "relative",
                  border: '1px solid black',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.boxShadow = '0 4px 24px 0 rgba(30, 41, 59, 0.12)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.currentTarget.style.boxShadow = value === options[rowIndex * 2 + 1]
                    ? '0 4px 24px 0 rgba(30, 41, 59, 0.18)'
                    : '';
                }}
              >
                <CustomText
                  style={{
                    fontSize: isMobile ? 16 : 20,
                    fontWeight: value === options[rowIndex * 2 + 1] ? "700" : "500",
                    fontFamily: "Nunito",
                    color: value === options[rowIndex * 2 + 1] ? colors.white : colors.gray[700],
                    textAlign: "center",
                    textShadow: value === options[rowIndex * 2 + 1]
                      ? '0 2px 8px rgba(30, 41, 59, 0.18)'
                      : undefined,
                    transition: "all 0.2s",
                  }}
                >
                  {options[rowIndex * 2 + 1]}
                </CustomText>
              </Pressable>
            )}
          </View>
        ))}
      </View>
    </View>
  );
};

const SectionedInfoCollection: React.FC<{
  onComplete: (answers: Record<string, string>) => void;
  onAnswerUpdate: (answers: Record<string, string>) => void;
  onSectionUpdate: (section: number) => void;
  onNavigationReady?: (navigation: { handleNext: () => void; handleBack: () => void }) => void;
}> = ({ onComplete, onAnswerUpdate, onSectionUpdate, onNavigationReady }) => {
  const { isMobile } = useResponsive();
  const [section, setSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const currentQuestion = INFO_COLLECTION_QUESTIONS[section];
  const currentAnswer = answers[currentQuestion.question];

  const handleAnswer = (value: string) => {
    const newAnswers = {
      ...answers,
      [currentQuestion.question]: value,
    };
    setAnswers(newAnswers);
    onAnswerUpdate(newAnswers);
  };

  const handleNext = useCallback(() => {
    if (section < NUM_SECTIONS - 1) {
      const newSection = section + 1;
      setSection(newSection);
      onSectionUpdate(newSection);
    } else {
      // All questions answered, complete the form
      onComplete(answers);
    }
  }, [section, onComplete, onSectionUpdate, answers]);

  const handleBack = useCallback(() => {
    if (section > 0) {
      const newSection = section - 1;
      setSection(newSection);
      onSectionUpdate(newSection);
    }
  }, [section, onSectionUpdate]);

  // Pass navigation functions to parent for mobile use
  useEffect(() => {
    if (onNavigationReady) {
      onNavigationReady({ handleNext, handleBack });
    }
  }, [onNavigationReady, handleNext, handleBack]);

  // Scroll to top when section changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [containerRef, section]);

  return (
    <View
      ref={containerRef}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xl,
        paddingLeft: isMobile ? spacing.lg : spacing.xxl,
        paddingRight: isMobile ? spacing.lg : spacing.xxl,
        marginBottom: spacing.xxl,
        scrollMarginTop: 150,
      }}
    >
      {/* Instructions card - only show on first section */}
      {section === 0 && (
        <View
          style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
            padding: isMobile ? spacing.md : spacing.xl,
            maxWidth: 900,
            width: "100%",
            marginBottom: spacing.xxl,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <CustomText
              style={{
                fontWeight: 700,
                fontSize: isMobile ? 24 : 36,
                fontFamily: "'Nunito', sans-serif",
              }}
            >
              📋 Complete Your Profile
            </CustomText>
          </View>

          <View
            style={{
              display: "flex",
              flexDirection: "column",
              flexWrap: "wrap",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <CustomText
              style={{
                fontSize: isMobile ? 16 : 24,
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 400,
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              Please answer the following questions to help us provide you with personalized financial guidance.
            </CustomText>
          </View>

          <CustomText
            style={{
              fontSize: isMobile ? 16 : 24,
              color: "#444",
              marginBottom: 28,
              fontFamily: "'Nunito', sans-serif",
            }}
          >
            Est. time to complete: 1 minute
          </CustomText>
        </View>
      )}

      {/* Current question */}
      <InfoCollectionQuestion
        question={currentQuestion.question}
        options={currentQuestion.options}
        value={currentAnswer}
        onChange={handleAnswer}
        onNext={handleNext}
      />

      {!isMobile && (
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            marginTop: spacing.xl,
            gap: spacing.lg,
          }}
        >
          {section > 0 && (
            <Pressable
              style={{
                backgroundColor: colors.gray[200],
                paddingVertical: isMobile ? spacing.sm : spacing.md,
                paddingHorizontal: isMobile ? spacing.lg : spacing.xl,
                borderRadius: 12,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onPress={handleBack}
            >
              <CustomText
                style={{
                  color: colors.dark,
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: "600",
                  fontFamily: "Nunito",
                }}
              >
                Back
              </CustomText>
            </Pressable>
          )}

          <Pressable
            style={{
              backgroundColor: currentAnswer ? "#748469" : colors.gray[300],
              paddingVertical: isMobile ? spacing.sm : spacing.md,
              paddingHorizontal: isMobile ? spacing.lg : spacing.xl,
              borderRadius: 12,
              cursor: currentAnswer ? "pointer" : "not-allowed",
              transition: "all 0.2s",
            }}
            disabled={!currentAnswer}
            onPress={handleNext}
          >
            <CustomText
              style={{
                color: colors.white,
                fontSize: isMobile ? 16 : 18,
                fontWeight: "600",
                fontFamily: "Nunito",
              }}
            >
              {section < NUM_SECTIONS - 1 ? "Next" : "Submit"}
            </CustomText>
          </Pressable>
        </View>
      )}
    </View>
  );
};

export default function ProfileInfoCollection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();

  // Add session check
  const { data: session, isLoading: isSessionLoading } = useAuthSession();
  useEffect(() => {
    if (!isSessionLoading && !session) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [session, isSessionLoading, navigate, location.pathname]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showThanks, setShowThanks] = useState<boolean>(false);
  const [currentAnswers, setCurrentAnswers] = useState<Record<string, string>>({});
  const [currentSection, setCurrentSection] = useState<number>(0);
  const [navigationFunctions, setNavigationFunctions] = useState<{ handleNext: () => void; handleBack: () => void } | null>(null);

  const { mutateAsync: updateCurrentProfile } = useUpdateCurrentProfile();

  const handleComplete = async (answers: Record<string, string>) => {
    setIsSubmitting(true);
    console.log("User information collected:", answers);

    try {
      // Transform answers to a simple key-value object for profile info
      // Sanitize the keys to remove special characters that might cause issues
      const profileInfo = Object.entries(answers).reduce((acc, [question, answer]) => {
        // Sanitize the question key by removing special characters and replacing with underscores
        const sanitizedKey = question
          .replace(/[?:]/g, '') // Remove question marks and colons
          .replace(/\s+/g, '_') // Replace spaces with underscores
          .replace(/[^a-zA-Z0-9_]/g, '_') // Replace any other special characters with underscores
          .toLowerCase(); // Convert to lowercase
        
        acc[sanitizedKey] = answer;
        return acc;
      }, {} as Record<string, string>);

      console.log("sending personalInfo to updateCurrentProfile", profileInfo);
      console.log("sending personalInfo type:", typeof profileInfo);
      console.log("sending personalInfo JSON:", JSON.stringify(profileInfo));

      // Update user's profile with personal information
      // First try with a minimal test
      try {
        console.log("Testing with minimal data first...");
        await updateCurrentProfile({
          info: {"test": "value"},
        });
        console.log("Minimal test successful, now trying full data...");
      } catch (minimalError) {
        console.error("Minimal test failed:", minimalError);
      }
      
      await updateCurrentProfile({
        info: profileInfo,
      });
      
      // Store personal info collection answers in localStorage
      localStorage.setItem('mpay_personalInfo', JSON.stringify(currentAnswers));
      console.log('Personal info stored in localStorage:', currentAnswers);
    } catch (error: any) {
      console.error("Failed to submit user information:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
      });
    } finally {
      setIsSubmitting(false);
      setShowThanks(true);
    }
  };

  const handleAnswerUpdate = (answers: Record<string, string>) => {
    setCurrentAnswers(answers);
  };

  const handleBackToPortal = () => {
    // Navigate back to client portal
    navigate("/client");
  };

  const ProgressFooter = () => {
    const answeredCount = Object.keys(currentAnswers).length;
    const progress = answeredCount / INFO_COLLECTION_QUESTIONS.length;
    const canGoBack = currentSection > 0;
    
    // Get current question and answer for mobile navigation
    const currentQuestion = INFO_COLLECTION_QUESTIONS[currentSection];
    const currentAnswer = currentAnswers[currentQuestion?.question];
    
    // Use navigation functions from SectionedInfoCollection
    const handleNext = navigationFunctions?.handleNext || (() => {});

    return (
      <View
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: colors.white,
          borderTop: `1px solid ${colors.gray[200]}`,
          padding: isMobile ? spacing.sm : spacing.md,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isMobile ? "space-around" : "center",
          gap: isMobile ? spacing.xs : spacing.md,
          zIndex: 100,
          boxShadow: "0 -1px 6px rgba(0,0,0,0.04)",
        }}
      >
        {isMobile ? (
          <>
            {/* Back Button */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                width: 80,
                justifyContent: "center",
              }}
            >
              <Pressable
                style={{
                  backgroundColor: colors.gray[200],
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.sm,
                  borderRadius: 8,
                  cursor: canGoBack ? "pointer" : "default",
                  width: "100%",
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  visibility: canGoBack ? "visible" : "hidden",
                }}
                onPress={navigationFunctions?.handleBack || (() => {})}
                disabled={!canGoBack}
              >
                <CustomText
                  style={{
                    color: colors.dark,
                    fontSize: 14,
                    fontWeight: "600",
                    fontFamily: "Nunito",
                  }}
                >
                  Back
                </CustomText>
              </Pressable>
            </View>
            {/* Progress section */}
            <View
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  width: isMobile ? 150 : 200,
                  height: 8,
                  backgroundColor: colors.gray[200],
                  borderRadius: 4,
                  overflow: "hidden",
                  flexShrink: 0,
                  marginHorizontal: isMobile ? 10 : 0,
                }}
              >
                <View
                  style={{
                    width: `${progress * 100}%`,
                    height: "100%",
                    backgroundColor: colors.primary,
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </View>
              <CustomText
                style={{
                  color: colors.gray[700],
                  fontSize: isMobile ? 13 : 16,
                  fontFamily: "Nunito",
                  whiteSpace: "nowrap",
                }}
              >
                {Math.round(progress * 100)}%
              </CustomText>
            </View>
            {/* Next Button */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                width: 80,
              }}
            >
              <Pressable
                style={{
                  width: "100%",
                  height: 32,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor:
                    currentAnswer ? "#748469" : colors.gray[300],
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.sm,
                  borderRadius: 8,
                  cursor: currentAnswer ? "pointer" : "not-allowed",
                  opacity: currentAnswer ? 1 : 0.5,
                }}
                onPress={handleNext}
                disabled={!currentAnswer}
              >
                <CustomText
                  style={{
                    color: colors.white,
                    fontSize: 14,
                    fontWeight: "600",
                    fontFamily: "Nunito",
                  }}
                >
                  {currentSection < NUM_SECTIONS - 1 ? "Next" : "Submit"}
                </CustomText>
              </Pressable>
            </View>
          </>
        ) : (
          <>
            <CustomText
              style={{
                color: colors.gray[700],
                fontSize: isMobile ? 14 : 16,
                fontFamily: "Nunito",
                whiteSpace: "nowrap",
              }}
            >
              {answeredCount} of {INFO_COLLECTION_QUESTIONS.length} answered
            </CustomText>
            <View
              style={{
                width: isMobile ? 150 : 200,
                height: 8,
                backgroundColor: colors.gray[200],
                borderRadius: 4,
                overflow: "hidden",
                flexShrink: 0,
                marginHorizontal: isMobile ? 10 : 0,
              }}
            >
              <View
                style={{
                  width: `${progress * 100}%`,
                  height: "100%",
                  backgroundColor: colors.primary,
                  transition: "width 0.3s ease-in-out",
                }}
              />
            </View>
            <CustomText
              style={{
                color: colors.gray[700],
                fontSize: isMobile ? 13 : 16,
                fontFamily: "Nunito",
                whiteSpace: "nowrap",
              }}
            >
              {Math.round(progress * 100)}%
            </CustomText>
          </>
        )}
      </View>
    );
  };

  return (
    <View style={{ flex: 1, position: "relative", minHeight: "100vh" }}>
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? spacing.sm : spacing.xl,
          paddingBottom: isMobile ? spacing.xxl : spacing.xl,
        }}
      >
        {!showThanks ? (
          <SectionedInfoCollection
            onComplete={handleComplete}
            onAnswerUpdate={handleAnswerUpdate}
            onSectionUpdate={setCurrentSection}
            onNavigationReady={setNavigationFunctions}
          />
        ) : (
          <View
            style={{
              backgroundColor: colors.white,
              borderRadius: 12,
              border: "1px solid black",
              padding: isMobile ? spacing.xl : spacing.xxl,
            }}
          >
            <CustomText
              style={{
                fontFamily: "Nunito",
                fontWeight: 400,
                fontSize: isMobile ? 24 : 36,
                textAlign: "center",
                marginBottom: isMobile ? 10 : 15,
              }}
            >
              Thank you for completing your profile! Your information has been saved.
            </CustomText>
            <View
              style={{
                display: "flex",
                flexDirection: "column",
                gap: spacing.md,
                marginTop: spacing.lg,
              }}
            >
              <Pressable
                onPress={handleBackToPortal}
                style={({ hovered }: { hovered: boolean }) => ({
                  backgroundColor: hovered
                    ? colors.gray[300]
                    : colors.gray[200],
                  paddingVertical: isMobile ? spacing.sm : spacing.md,
                  paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                })}
              >
                <CustomText
                  style={{
                    color: colors.dark,
                    fontSize: isMobile ? 18 : 24,
                    fontWeight: "600",
                    fontFamily: typography.fontFamily.body,
                  }}
                >
                  Back to portal
                </CustomText>
              </Pressable>
            </View>
          </View>
        )}
      </View>

      {isSubmitting && (
        <View
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: colors.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CustomText
            style={{
              fontSize: isMobile ? 24 : 36,
              fontWeight: "600",
              color: colors.dark,
              fontFamily: "Nunito",
              textAlign: "center",
            }}
          >
            Submitting your information...
          </CustomText>
        </View>
      )}

      {!showThanks && <ProgressFooter />}
    </View>
  );
} 